<script setup lang="ts">
/* import type { Job, CareersResultsLabels } from '@pkg/types/careers'; */
import type { PortableTextBlock } from '@sanity/types';
import { THEME } from '@pkg/config/misc/theme';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
/* import type { RouteLocationNormalized } from 'vue-router';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { Option } from '../../types/formTypes';
import JobsList from './JobsList.vue';
import CareersForm from '~/app/features/jobFinder/components/CareersForm.vue'; */
/* import { useCareersForm } from '~/app/features/jobFinder/composables/useCareersForm';
import { useCareers } from '~/app/composables/useCareers';
import { fetchCareers, fetchCities, ALL_OPTION_VALUE } from '~/apis/external/careers';
import { CAREERS_LIST_LIMIT_PER_PAGE } from '~/app/types/listingTypes';
import { useLocale } from '~/app/composables/useLocale'; */

// Careers.vue renders multiple root nodes (the section + the Ashby iframe),
// so Vue does not auto-apply fallthrough attrs. Bind the anchor id explicitly.

/* const props =  */defineProps<{
  theme: THEME,
  title: string,
  anchor?: string,
  description?: Array<PortableTextBlock>,
  /* pretitle: string,
  departmentFieldLabel: string,
  departmentFieldPlaceholder: string,
  countryFieldLabel: string,
  countryFieldPlaceholder: string,
  cityFieldLabel: string,
  cityFieldPlaceholder: string,
  submitButtonLabel: string,
  clearButtonLabel: string,
  resultsLabels: CareersResultsLabels,
  defaultResultsLabels: CareersResultsLabels,
  sanityKey?: string, */
}>();
/*
const labelsForResults = computed(() => props.resultsLabels || props.defaultResultsLabels);

const { jobsList, departments, totalJobsListNumber } = await fetchCareers(labelsForResults.value);
const { handleSubmit, handleReset, setValues, values } = useCareersForm();
const router = useRouter();
const route = useRoute();
const { options } = useLocale();

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const countries = computed(() => {
  const localeCountries =
  Object.values(options).map(market => ({
    title: market.country,
    value: market.countryIso,
  }));
  return [...localeCountries, {
    title: capitalize(labelsForResults.value?.allCountriesLabel || ALL_OPTION_VALUE),
    value: ALL_OPTION_VALUE,
  }];
});

const selectedDepartment = ref<string>();
const selectedCountry = ref<string>();
const selectedCity = ref<string>();
const filteredJobsList = ref<Job[]>(jobsList);
const resultsText = ref();
const currentPageNumber = ref(1);
const jobListTotalNumber = ref(totalJobsListNumber);
const careersSection = ref<HTMLElement>();
const enableCityField = ref(false);
const citiesList = ref<Option[]>();

// TEMPORARY STARRED SURVEY SCRIPT - please delete later
const { locale } = useI18n();
const { public: { domain } } = useRuntimeConfig();

const showCareersSurvey = (!domain.includes('localhost')) && (locale.value === LOCALE_CODE.EN_GB ||
    locale.value === LOCALE_CODE.EN_US ||
    locale.value === LOCALE_CODE.EN_CA ||
    locale.value === LOCALE_CODE.EN_AU ||
    locale.value === LOCALE_CODE.EN_IE);

if (showCareersSurvey) {
  useHead({
    script: [
      {
        id: 'starred-survey-career-page-script',
        src: 'https://cdn.starred.com/react/survey-store/scp/index.js?uid=1b5f324f-d68a-40cf-add9-c036dfff6a30&delay=30', // The script URL
        async: true,
      },
      {
        type: 'text/javascript',
      },
    ],
  });
}

const updateResults = async(department?: string, country?: string, city?: string) => {
  const { jobsList, totalJobsListNumber } = await fetchCareers(undefined, department, country, city, currentPageNumber.value);
  const { updatedJobList, resultsMessage } = useCareers(countries.value, totalJobsListNumber, jobsList, department, country, city, labelsForResults.value);
  jobListTotalNumber.value = totalJobsListNumber;
  filteredJobsList.value = updatedJobList;
  resultsText.value = resultsMessage;
};

const scrollToList = () => {
  setTimeout(() => careersSection.value?.scrollIntoView({
    behavior: 'instant',
    block: 'start',
  }));
};

const checkPageNumber = (page: string) => {
  const pageNumber = parseInt(page);
  if (pageNumber < 1) { return 1; }
  const numberOfPages = Math.ceil(jobListTotalNumber.value / CAREERS_LIST_LIMIT_PER_PAGE);
  if (pageNumber > numberOfPages) {
    handlePagination(1);
    scrollToList();
    return 1;
  }
  return pageNumber;
};

const setQueryParamsAsValues = (route: RouteLocationNormalized) => {
  const department = typeof route.query?.department === 'string' ? route.query.department : undefined;
  const country = typeof route.query?.country === 'string' ? route.query.country : undefined;
  const city = typeof route.query?.city === 'string' ? route.query.city : undefined;
  const pageParam = props.sanityKey ? `page_${props.sanityKey}` : 'page_careers';
  const page = typeof route.query?.[pageParam] === 'string' ? checkPageNumber(route.query[pageParam]) : undefined;
  currentPageNumber.value = page || 1;
  if (department || country || route.fullPath === '/careers#open-roles') {
    scrollToList();
  }
  setValues({ department, countryIso: country, city });
  updateResults(department, country, city);
};

onMounted(() => {
  setQueryParamsAsValues(route);
});

const onSubmit = handleSubmit((values) => {
  currentPageNumber.value = 1;
  updateResults(values.department, values.countryIso, values.city);
  scrollToList();
  const pageParam = props.sanityKey ? `page_${props.sanityKey}` : 'page_careers';
  router.push({
    query: { department: values.department, country: values.countryIso, city: values.city, [pageParam]: currentPageNumber.value },
    hash: route.hash,
  });
});

const handlePagination = (selectedPage: number) => {
  currentPageNumber.value = selectedPage;
  updateResults(values.department, values.countryIso);
  const pageParam = props.sanityKey ? `page_${props.sanityKey}` : 'page_careers';
  router.push({
    query: { department: values.department, country: values.countryIso, city: values.city, [pageParam]: currentPageNumber.value },
    hash: route.hash,
  });
  scrollToList();
};

const onClear = () => {
  router.push({ query: { department: undefined, country: undefined, city: undefined } });
  currentPageNumber.value = 1;
  updateResults(ALL_OPTION_VALUE, ALL_OPTION_VALUE, ALL_OPTION_VALUE);
  enableCityField.value = false;
  handleReset();
};

const setCityValue = async(newCountry: string) => {
  citiesList.value = await fetchCities(newCountry, labelsForResults.value.allCitiesLabel);
  enableCityField.value = citiesList.value.length > 0 && !(newCountry === ALL_OPTION_VALUE || newCountry === undefined);
  selectedCountry.value = newCountry;
  selectedCity.value = ALL_OPTION_VALUE;
  const isCityOld = values.city === undefined || !!citiesList.value.find((city: Option) => city.value === values.city);
  if (!isCityOld) {
    setValues({ city: ALL_OPTION_VALUE });
  };
};

watch(values, (newValue) => {
  if (newValue.countryIso) {
    setCityValue(newValue.countryIso);
  }
}); */
</script>

<template>
  <PartialSection :anchor="anchor" :theme="theme || THEME.DARK">
    <div ref="careersSection" class="wrapper desk:max-w-[996px] mb-[-150px]">
      <div class="bg-primary-30 flex flex-col justify-center align-middle py-72 px-20 tablet-wide:px-80 tablet-wide:pv-100">
        <h2 class="flex align-middle w-full flex-col justify-center pb-32 text-center">
          <span class="text-h4 uppercase text-yellow-30 self-center">
            {{ pretitle }}
          </span>
          <span class="text-h2 uppercase text-white self-center max-w-[350px]">
            {{ title }}
          </span>
        </h2>

        <!--<CareersForm
          :countries="countries"
          :departments="departments"
          :cities="citiesList"
          :selected-department="selectedDepartment"
          :selected-country="selectedCountry"
          :selected-city="selectedCity"
          :department-field-label="departmentFieldLabel"
          :department-field-placeholder="departmentFieldPlaceholder"
          :country-field-label="countryFieldLabel"
          :country-field-placeholder="countryFieldPlaceholder"
          :city-field-label="cityFieldLabel"
          :city-field-placeholder="cityFieldPlaceholder"
          :enable-city-field="enableCityField"
          :submit-button-label="submitButtonLabel"
          :clear-button-label="clearButtonLabel"
          :on-submit="onSubmit"
          :results-text="resultsText"
          :on-clear="onClear"
        />
        <JobsList :jobs="filteredJobsList" />
        <div v-if="jobListTotalNumber > CAREERS_LIST_LIMIT_PER_PAGE" class="c-listing__pagination text-center">
          <BasePagination
            v-model="currentPageNumber"
            :total-posts="jobListTotalNumber"
            :items-per-page="CAREERS_LIST_LIMIT_PER_PAGE"
            :listing-key="sanityKey || 'careers'"
            @update:page="handlePagination"
          />
        </div>-->
        <div v-if="description" class="wrapper wrapper--content inner-block-base-text text-center mb-24">
          <PortableEditor :content="description" />
        </div>
      </div>
    </div>
  </PartialSection>
  <div
    :data-theme="THEME.LIGHT"
  >
    <iframe
      src="https://jobs.ashbyhq.com/toogoodtogo?embed=js"
      width="100%"
      height="900px"
      style="border: none;"
      title="Careers Page"
    />
  </div>
</template>
